import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

const InstallPWA = ({ language = 'fr' }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return; // Already installed as PWA
    }

    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Listen for beforeinstallprompt (Android/Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show banner after 30 seconds on the site
      setTimeout(() => {
        setShowInstallBanner(true);
      }, 30000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Show iOS instructions after some time
    if (isIOSDevice) {
      const hasSeenIOSPrompt = localStorage.getItem('ikigai-ios-prompt-seen');
      if (!hasSeenIOSPrompt) {
        setTimeout(() => {
          setShowInstallBanner(true);
        }, 30000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      // User accepted install
    }
    
    setDeferredPrompt(null);
    setShowInstallBanner(false);
  };

  const dismissBanner = () => {
    setShowInstallBanner(false);
    if (isIOS) {
      localStorage.setItem('ikigai-ios-prompt-seen', 'true');
    }
  };

  if (!showInstallBanner) return null;

  return (
    <>
      {/* Install Banner */}
      <div className="fixed bottom-20 left-4 right-4 z-50 animate-slide-up">
        <div className="bg-white rounded-2xl shadow-2xl border border-stone-100 p-4 max-w-md mx-auto">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF6B6B] to-[#FFE66D] flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-stone-900 text-sm">
                {language === 'fr' ? 'Installe l\'app Ikigai' : 'Install Ikigai App'}
              </h3>
              <p className="text-xs text-stone-500 mt-0.5">
                {language === 'fr' 
                  ? 'Accède à ton parcours depuis ton écran d\'accueil'
                  : 'Access your journey from your home screen'}
              </p>
              <div className="flex gap-2 mt-3">
                <Button
                  onClick={handleInstallClick}
                  size="sm"
                  className="bg-[#FF6B6B] hover:bg-[#FF5252] text-white rounded-lg text-xs h-8 px-3"
                >
                  <Download className="w-3 h-3 mr-1" />
                  {language === 'fr' ? 'Installer' : 'Install'}
                </Button>
                <Button
                  onClick={dismissBanner}
                  variant="ghost"
                  size="sm"
                  className="text-stone-500 text-xs h-8"
                >
                  {language === 'fr' ? 'Plus tard' : 'Later'}
                </Button>
              </div>
            </div>
            <button
              onClick={dismissBanner}
              className="text-stone-400 hover:text-stone-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* iOS Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50 p-4">
          <div className="bg-white rounded-t-3xl w-full max-w-md p-6 animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-lg font-semibold text-stone-900">
                {language === 'fr' ? 'Installer sur iPhone/iPad' : 'Install on iPhone/iPad'}
              </h3>
              <button
                onClick={() => setShowIOSInstructions(false)}
                className="text-stone-400 hover:text-stone-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#FF6B6B]/10 flex items-center justify-center text-[#FF6B6B] font-bold text-sm">
                  1
                </div>
                <div>
                  <p className="text-stone-800 text-sm">
                    {language === 'fr' 
                      ? 'Appuie sur le bouton Partager'
                      : 'Tap the Share button'}
                  </p>
                  <p className="text-stone-500 text-xs mt-0.5">
                    {language === 'fr' 
                      ? '(icône carrée avec flèche vers le haut)'
                      : '(square icon with arrow pointing up)'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#FF6B6B]/10 flex items-center justify-center text-[#FF6B6B] font-bold text-sm">
                  2
                </div>
                <p className="text-stone-800 text-sm">
                  {language === 'fr' 
                    ? 'Fais défiler et appuie sur "Sur l\'écran d\'accueil"'
                    : 'Scroll down and tap "Add to Home Screen"'}
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#FF6B6B]/10 flex items-center justify-center text-[#FF6B6B] font-bold text-sm">
                  3
                </div>
                <p className="text-stone-800 text-sm">
                  {language === 'fr' 
                    ? 'Appuie sur "Ajouter" en haut à droite'
                    : 'Tap "Add" in the top right'}
                </p>
              </div>
            </div>

            <Button
              onClick={() => setShowIOSInstructions(false)}
              className="w-full mt-6 bg-[#FF6B6B] hover:bg-[#FF5252] text-white rounded-xl h-12"
            >
              {language === 'fr' ? 'J\'ai compris' : 'Got it'}
            </Button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default InstallPWA;
